/**
 * OffSiteWorkApprovals — Wave 3.2 live-API wrapper around <ApprovalList>.
 *
 * Backend data shape for type='off_site_work' (not in seed_test_data.py —
 * frontend mock defines: date, location, work_type, reason). Renders
 * whichever subset the payload contains.
 */

import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import ApprovalList, { ApprovalColumn, ApprovalFormField } from './ApprovalList';
import { useTranslation } from 'react-i18next';

function pickStr(data: unknown, key: string): string {
  return typeof data === 'object' && data !== null && key in data
    ? String((data as Record<string, unknown>)[key] ?? '')
    : '';
}

/** "2026-08-28 → 2026-08-31", or the single date when the span is one day.
 *  Falls back to the LEGACY `date` key so rows written before 2026-08-25 — and
 *  any request from an app build that predates it — still read correctly here
 *  instead of showing "—". */
function spanLabel(data: unknown): string {
  const start = pickStr(data, 'start_date') || pickStr(data, 'date');
  const end = pickStr(data, 'end_date') || start;
  if (!start) return '—';
  return start === end ? start : `${start} → ${end}`;
}

const OffSiteWorkApprovals: React.FC = () => {
  const { t } = useTranslation();

  const columns = useMemo<ApprovalColumn[]>(() => [
    { label: t('attendance:approvals.offSiteWork.colDates'), render: (r) => spanLabel(r.data) },
    {
      // Duration, with the working-day count beside it only when the two differ
      // — which is exactly when the difference is the thing worth reading (a
      // span bracketing a weekend or a public holiday).
      label: t('attendance:approvals.offSiteWork.colDays'),
      render: (r) => {
        const days = pickStr(r.data, 'days');
        const working = pickStr(r.data, 'working_days');
        if (!days) return '—';
        return working && working !== days
          ? t('attendance:approvals.offSiteWork.daysWithWorking', { days, working })
          : days;
      },
    },
    {
      label: t('attendance:approvals.offSiteWork.colLocation'),
      render: (r) => pickStr(r.data, 'location') || '—',
    },
    {
      label: t('attendance:approvals.offSiteWork.colReason'),
      render: (r) => (
        <Typography variant="caption" color="text.secondary">
          {pickStr(r.data, 'reason') || '—'}
        </Typography>
      ),
    },
  ], [t]);

  const createFields = useMemo<ApprovalFormField[]>(() => [
    // A SPAN, not a single day (owner 2026-08-25): a three-day site visit was
    // unrecordable on every surface. Same `start_date` / `end_date` keys the
    // worker portal and mobile now write.
    { key: 'start_date', label: t('attendance:approvals.offSiteWork.formStartDate'), type: 'date', required: true, span: 6 },
    { key: 'end_date', label: t('attendance:approvals.offSiteWork.formEndDate'), type: 'date', required: true, span: 6 },
    // The DURATION: an inclusive subtraction of the two dates, which is exactly
    // what the server stores in `days`, so this number and the stored one agree.
    {
      key: 'days',
      label: t('attendance:approvals.offSiteWork.colDays'),
      type: 'derived_days',
      derivedFromDates: ['start_date', 'end_date'],
      span: 6,
    },
    // The WORKING-day count, from the server for THIS worker. Cannot be computed
    // here: rest days live on the org's AttendanceRule and holidays on a
    // per-worker calendar.
    {
      key: 'working_days',
      label: t('attendance:approvals.offSiteWork.formWorkingDays'),
      type: 'derived_working_days',
      derivedFromDates: ['start_date', 'end_date'],
      span: 6,
      placeholder: t('attendance:approvals.helperWorkingDays'),
    },
    // `work_type` was here as a REQUIRED select of three options. Nothing reads it —
    // zero hits for the key anywhere in `app/`, and neither the worker portal nor mobile
    // has ever written it — so its column showed "—" for every worker-filed request while
    // the admin was forced to pick a value no part of the product consumes. Deleted with
    // its column (2026-08-25); the standing rule is that a capability the engine does not
    // consume must not appear in the UI.
    //
    // `start_time` / `end_time` replace it, because the other two surfaces DO write them
    // and this one silently dropped that half of the request. They are the window worked
    // on EACH day of the span, which is why their labels say "daily".
    { key: 'start_time', label: t('attendance:approvals.offSiteWork.formDailyStartTime'), type: 'time', span: 6 },
    { key: 'end_time', label: t('attendance:approvals.offSiteWork.formDailyEndTime'), type: 'time', span: 6 },
    // `place`, not `text` — same reason as the trip destination above.
    { key: 'location', label: t('attendance:approvals.offSiteWork.colLocation'), type: 'place', required: true, placeholder: t('attendance:approvals.offSiteWork.locationPlaceholder') },
    { key: 'reason', label: t('attendance:approvals.offSiteWork.colReason'), type: 'textarea', required: true },
  ], [t]);

  return (
    <ApprovalList
      type="off_site_work"
      title={t('attendance:approvals.offSiteWorkTitle')}
      description={t('attendance:approvals.offSiteWorkDescription')}
      recordTitle={t('attendance:approvals.offSiteWork.recordTitle')}
      filterLabel={t('attendance:approvals.filterLabel')}
      columns={columns}
      createFields={createFields}
    />
  );
};

export default OffSiteWorkApprovals;
